import React, { useLayoutEffect, useState } from "react";
import { StyleSheet, Text, View, ScrollView } from "react-native";
import { colors } from "src/assets";
import {
  Button,
  UserCard,
  ReviewCarousel,
  RelatedOffersList,
  SoundRecorder,
  SoundRecoredVariant,
  ActionDialog,
} from "src/components";
import { useNavigation, useRoute } from "@react-navigation/native";
import { stringDateFormat } from "src/utils/date";
import {
  useUser,
  useReviewByUserAsServiceOwner,
  useRelatedOffers,
  useBlockActions,
  useCognito,
} from "src/hooks";
import { IServiceFilters } from "src/domains";
import LocalText from "src/local";
import alert from "src/components/alert";
import { RootStackScreenProps } from "src/navigation/types";

export type OwnerNavigationParam = {
  id: string;
};

export const OwnerScreen: React.FC = () => {
  const navigation =
    useNavigation<RootStackScreenProps<"Owner">["navigation"]>();
  const route = useRoute<RootStackScreenProps<"Owner">["route"]>();
  const [user] = useUser(route.params.id);
  const [cognitoUsername] = useCognito();
  const [reviewByUserAsServiceOwnerItems] = useReviewByUserAsServiceOwner(
    route.params.id
  );
  const [relatedOffersFilter] = useState<IServiceFilters | undefined>({
    ownerIds: [route.params.id],
    isBlocked: false,
    isDisabled: false,
  });
  const [relatedOffers] = useRelatedOffers(relatedOffersFilter);
  const [moreDialogOpen, setMoreDialogOpen] = useState<boolean>(false);
  const { block } = useBlockActions();

  useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => {
        if (route.params.id !== cognitoUsername && cognitoUsername) {
          return (
            <Button
              onPress={() => {
                setMoreDialogOpen(true);
              }}
              variant={"icon"}
              icon={"more-circle"}
              iconSize={24}
              iconColor={colors.chokuRed}
              containerStyle={{ marginRight: 16 }}
            />
          );
        } else {
          return null;
        }
      },
    });
  }, [navigation, cognitoUsername, route.params.id]);

  const moreActionSheetHandler = async (index: number) => {
    if (index === 0) {
      await block(route.params.id);
      alert(
        LocalText.screens.owner.blockAlertTitle,
        undefined,
        [
          {
            text: LocalText.common.ok,
            style: "cancel",
            onPress: () => navigation.goBack(),
          },
        ],
        { cancelable: false }
      );
    }
  };

  return (
    <View style={styles.container}>
      {user ? (
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          <UserCard
            contanerStyle={styles.owner}
            source={user?.image ? { uri: user?.image } : undefined}
            status={user?.status}
            name={user?.name}
            birthday={user?.birthday}
            gender={user?.gender}
            prefecture={user?.address?.prefecture}
            isVerified={user?.identificationStatus === "APPROVED"}
            lastLoginAt={user?.lastLoginAt}
          />
          <View style={styles.row}>
            <Text style={styles.createdAt}>
              {LocalText.screens.owner.createdAt}
              {stringDateFormat(user.createdAt || "")}
            </Text>
          </View>
          {user.aboutTalk ? (
            <React.Fragment>
              <Text style={styles.section}>
                {LocalText.screens.owner.aboutTalk}
              </Text>
              <Text style={styles.text}>{user.aboutTalk}</Text>
            </React.Fragment>
          ) : null}
          {user.aboutProfile ? (
            <React.Fragment>
              <Text style={styles.section}>
                {LocalText.screens.owner.aboutProfile}
              </Text>
              <Text style={styles.text}>{user.aboutProfile}</Text>
            </React.Fragment>
          ) : null}
          {user.description ? (
            <React.Fragment>
              <Text style={styles.section}>
                {LocalText.screens.owner.description}
              </Text>
              <Text style={styles.text}>{user.description}</Text>
            </React.Fragment>
          ) : null}
          {/* {user.voice && (
            <SoundRecorder
              variant={SoundRecoredVariant.Player}
              source={user.voice}
            />
          )} */}
          {reviewByUserAsServiceOwnerItems.length > 0 ? (
            <ReviewCarousel
              title={LocalText.screens.owner.review}
              reviews={reviewByUserAsServiceOwnerItems}
              onMore={() =>
                navigation.push("ReviewByUserAsServiceOwner", {
                  id: route.params.id,
                  title: LocalText.screens.owner.review,
                  notFound: LocalText.screens.owner.reviewNotFound,
                })
              }
            />
          ) : null}
          {relatedOffers.length > 0 ? (
            <RelatedOffersList
              offers={relatedOffers}
              onOffer={(offer) =>
                navigation.push("Offer", { offerId: offer.id })
              }
            />
          ) : null}
        </ScrollView>
      ) : null}
      <ActionDialog
        open={moreDialogOpen}
        onClose={() => setMoreDialogOpen(false)}
        onAction={(index) => {
          setMoreDialogOpen(false);
          moreActionSheetHandler(index);
        }}
        items={[LocalText.screens.owner.block].map((key) => ({
          key,
          label: key,
        }))}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: colors.white,
  },
  scrollView: {
    flex: 1,
  },
  createdAt: {
    fontSize: 12,
    fontWeight: "600",
    color: colors.chokuGray5,
  },
  row: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  section: {
    fontSize: 12,
    lineHeight: 18,
    color: colors.chokuGray,
    paddingTop: 16,
    paddingHorizontal: 16,
  },
  text: {
    fontSize: 12,
    lineHeight: 18,
    color: "#333333",
    paddingTop: 8,
    paddingHorizontal: 16,
  },
  owner: {
    marginTop: 16,
    marginHorizontal: 16,
    marginBottom: 8,
  },
});
